import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";

const MagazinePage = () => {
  const [items, setItems] = useState([]);

  useEffect(() => { (async () => setItems(await api.magazines()))(); }, []);

  return (
    <div data-testid="magazine-page">
      {/* Header */}
      <section className="pt-14 md:pt-20 pb-8 max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="overline mb-6">The Magazine</div>
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="font-display font-normal text-ink text-6xl md:text-7xl lg:text-[104px] leading-[0.94] tracking-[-0.02em]"
        >
          Magazines
        </motion.h1>
        <p className="mt-6 max-w-2xl text-ink-soft text-base md:text-lg leading-relaxed">
          Every edition of World Visionary Times profiles the leaders, founders and visionaries shaping global business — reported with the discipline of a printed masthead and printed on paper worth keeping.
        </p>

        <div className="hairline mt-10" />
      </section>

      {/* Grid */}
      <section className="max-w-[1440px] mx-auto px-5 md:px-10 pb-24 md:pb-32">
        {items.length === 0 ? (
          <div className="py-24 text-center">
            <div className="font-display text-ink text-3xl md:text-4xl">No editions available yet.</div>
            <p className="mt-4 text-ink-soft">New editions will appear here as they are published.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14 md:gap-y-16" data-testid="magazine-grid">
            {items.map((m, i) => (
              <motion.article
                key={m.id}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`magazine-card-${m.slug}`}
                className="group"
              >
                <a
                  href="#"
                  onClick={(e) => e.preventDefault()}
                  data-testid={`magazine-cover-link-${m.slug}`}
                  aria-label={m.title}
                  className="block cursor-pointer"
                >
                  <div className="relative aspect-[4/5] overflow-hidden bg-cream shadow-[0_30px_60px_-25px_rgba(15,15,16,0.28)] border border-ink-line">
                    <img
                      src={m.cover_image}
                      alt={`${m.title} cover`}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.04]"
                    />
                    <div className="absolute inset-0 ring-1 ring-inset ring-black/5 pointer-events-none" />
                  </div>
                </a>

                <h3 className="mt-6 md:mt-7 font-display text-ink text-xl md:text-2xl leading-[1.2] group-hover:text-gold-dark transition-colors text-center">
                  <a
                    href="#"
                    onClick={(e) => e.preventDefault()}
                    data-testid={`magazine-title-${m.slug}`}
                    className="cursor-pointer"
                  >
                    {m.caption || m.title}
                  </a>
                </h3>
              </motion.article>
            ))}
          </div>
        )}
      </section>

      {/* Bottom CTA — "There's more for you" */}
      <section className="border-t border-ink-line bg-cream" data-testid="magazine-cta-section">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline mb-5">There{"\u2019"}s more for you</div>
            <h2 className="font-display font-normal text-ink text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-[-0.01em]">
              Share your story with our community.
            </h2>
            <p className="mt-6 text-ink-soft text-base md:text-lg leading-relaxed max-w-xl">
              Get featured in an upcoming edition of World Visionary Times to showcase your journey and share your work with a global readership of leaders and operators.
            </p>
          </div>
          <div className="flex flex-wrap gap-4 md:justify-end">
            <Link
              to="/get-featured"
              data-testid="magazine-cta-featured"
              className="inline-flex items-center gap-3 px-8 py-4 bg-ink text-paper font-medium text-sm tracking-wide rounded-full hover:bg-gold-dark transition-colors"
            >
              Get Featured <ArrowRight size={16} />
            </Link>
            <Link
              to="/contact"
              data-testid="magazine-cta-contact"
              className="inline-flex items-center gap-3 px-8 py-4 border border-ink text-ink font-medium text-sm tracking-wide rounded-full hover:bg-ink hover:text-paper transition-colors"
            >
              Talk to Us <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MagazinePage;
